import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const fromQuickAddCard = event.target instanceof Element && !!event.target.closest('quick-add-component');
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !fromQuickAddCard && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

function shippingSchedule(now = new Date()) {
  const zone = new Intl.DateTimeFormat('en-GB', {timeZone:'Europe/Copenhagen',year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit',hourCycle:'h23'});
  const parts = date => Object.fromEntries(zone.formatToParts(date).filter(p => p.type !== 'literal').map(p => [p.type, Number(p.value)]));
  const p = parts(now);
  const day = new Date(Date.UTC(p.year,p.month-1,p.day));
  const key = date => date.toISOString().slice(0,10);
  const holidays = year => {
    const a=year%19,b=Math.floor(year/100),c=year%100,d=Math.floor(b/4),e=b%4,f=Math.floor((b+8)/25),g=Math.floor((b-f+1)/3),h=(19*a+b-d-g+15)%30,i=Math.floor(c/4),k=c%4,l=(32+2*e+2*i-h-k)%7,m=Math.floor((a+11*h+22*l)/451),month=Math.floor((h+l-7*m+114)/31),date=(h+l-7*m+114)%31+1;
    const easter=Date.UTC(year,month-1,date);
    return new Set([year+'-01-01',year+'-12-25',year+'-12-26',...[-3,-2,0,1,39,49,50].map(n=>key(new Date(easter+n*86400000)))]);
  };
  const working = date => ![0,6].includes(date.getUTCDay()) && !holidays(date.getUTCFullYear()).has(key(date));
  const sameDay = working(day) && p.hour < 16;
  const target = new Date(day);
  if (!sameDay) { do { target.setUTCDate(target.getUTCDate()+1); } while (!working(target)); }
  const localCutoff = Date.UTC(target.getUTCFullYear(),target.getUTCMonth(),target.getUTCDate(),16);
  let cutoff = localCutoff;
  for(let n=0;n<3;n++) { const q=parts(new Date(cutoff)); cutoff += localCutoff-Date.UTC(q.year,q.month-1,q.day,q.hour,q.minute,q.second); }
  const seconds = Math.max(0,Math.ceil((cutoff-now.getTime())/1000));
  const countdown = [Math.floor(seconds/3600),Math.floor(seconds/60)%60,seconds%60].map(n=>String(n).padStart(2,'0')).join(':');
  const daysAway = Math.round((target-day)/86400000);
  const weekday = new Intl.DateTimeFormat('da-DK',{weekday:'long',timeZone:'UTC'}).format(target);
  const label = sameDay ? 'i dag' : daysAway===1 ? 'i morgen' : weekday;
  const dateLabel = new Intl.DateTimeFormat('da-DK',{weekday:'long',day:'numeric',month:'long',timeZone:'UTC'}).format(target);
  const deliveryDates=[];
  const deliveryDay=new Date(day);
  for(let i=0;i<3;i++){do{deliveryDay.setUTCDate(deliveryDay.getUTCDate()+1);}while(!working(deliveryDay));deliveryDates.push(new Date(deliveryDay));}
  const shortDate=new Intl.DateTimeFormat('da-DK',{day:'numeric',month:'short',timeZone:'UTC'});
  const deliveryRange=shortDate.formatRange(deliveryDates[0],deliveryDates[2]);
  return {deliveryRange,countdown,label,dateLabel,cutoff:new Date(cutoff).toISOString(),dispatchDate:key(target),sameDay};
}

class PelsfredCartClock extends HTMLElement {
 connectedCallback(){const update=()=>{const state=shippingSchedule();this.querySelectorAll('[data-dispatch-countdown]').forEach(el=>el.textContent=state.countdown);this.querySelectorAll('[data-dispatch-label]').forEach(el=>el.textContent=state.label);this.querySelectorAll('[data-delivery-range]').forEach(el=>el.textContent=state.deliveryRange);this.removeAttribute('title');};update();this.timer=setInterval(update,1000);}
 disconnectedCallback(){clearInterval(this.timer);}
}
if(!customElements.get('pelsfred-cart-clock'))customElements.define('pelsfred-cart-clock',PelsfredCartClock);
class PelsfredCartSuggestions extends HTMLElement {
  connectedCallback() { this.frame=requestAnimationFrame(()=>this.initialize()); }
  initialize() {
    this.abort?.abort();this.observer?.disconnect();this.mutations?.disconnect();
    this.hidden=false;
    this.track = this.querySelector('.pf-cart__recommendations');
    this.abort = new AbortController();
    const signal = this.abort.signal;
    if (!this.track) {this.hidden=true;return;}
    const update=()=>{
      this.track=this.querySelector('.pf-cart__recommendations');
      const dots=this.querySelector('[data-pf-dots]');
      this.hidden=!this.track?.children.length;
      if(this.hidden || !dots)return;
      if(dots.children.length!==this.track.children.length)dots.replaceChildren(...Array.from(this.track.children,()=>document.createElement('i')));
      const index=Math.round(this.track.scrollLeft/(this.track.children[0].getBoundingClientRect().width+10));
      [...dots.children].forEach((dot,i)=>dot.classList.toggle('is-active',i===index));
      this.querySelector('[data-pf-prev]').disabled=this.track.scrollLeft<2;
      this.querySelector('[data-pf-next]').disabled=this.track.scrollLeft+this.track.clientWidth>=this.track.scrollWidth-3;
    };
    this.track.addEventListener('scroll',update,{signal,passive:true});
    this.addEventListener('click',async event=>{
      const nav=event.target.closest('[data-pf-next],[data-pf-prev]');
      if(nav){this.track.scrollBy({left:(this.track.children[0].getBoundingClientRect().width+10)*(nav.hasAttribute('data-pf-next')?1:-1),behavior:matchMedia('(prefers-reduced-motion:reduce)').matches?'instant':'smooth'});return;}
      const button=event.target.closest('[data-pf-add]');
      if(!button||this.busy)return;
      this.busy=true;button.disabled=true;
      const status=this.querySelector('[data-pf-status]'); status.textContent='Tilføjer…';
      const deferred=CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({action:'add',context:'cart',lines:[{merchandiseId:button.dataset.pfAdd,quantity:1}],promise:deferred.promise}));
      try {
        const response=await fetch(`${window.Shopify.routes.root}cart/add.js`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:[{id:Number(button.dataset.pfAdd),quantity:1}],sections:'cart-drawer-section'})});
        const result=await response.json(); if(!response.ok||result.status)throw Error(result.description||'Varen kunne ikke tilføjes. Prøv igen.');
        const cartResponse=await fetch(`${window.Shopify.routes.root}cart.js`,{cache:'no-store'});
        if(!cartResponse.ok)throw Error('Kurven kunne ikke opdateres. Genindlæs siden.');
        const cart=await cartResponse.json();
        deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{items:cart.items,sections:result.sections,source:'pelsfred-cart-suggestions',didError:false}});
        status.textContent='Tilføjet til kurven';
      }catch(error){deferred.reject(error);status.textContent=error.message;}
      finally{this.busy=false;button.disabled=false;}
    },{signal});
    this.observer=new ResizeObserver(update);this.observer.observe(this.track);
    this.mutations=new MutationObserver(update);this.mutations.observe(this,{childList:true,subtree:true});requestAnimationFrame(update);
  }
  disconnectedCallback(){cancelAnimationFrame(this.frame);this.abort?.abort();this.observer?.disconnect();this.mutations?.disconnect();}
}
if(!customElements.get('pelsfred-cart-suggestions'))customElements.define('pelsfred-cart-suggestions',PelsfredCartSuggestions);

// A real, persistent cart expiry. The deadline changes only after a successful add.
const pfCartExpiryKey = 'pelsfred-cart-expiry-v1';
const pfCartDuration = 10 * 60 * 1000;
let pfCartMemory = null;
let pfCartPending = 0;
let pfCartClearing = false;
let pfCartRetryAt = 0;
function pfReadExpiry() {
  try { return JSON.parse(localStorage.getItem(pfCartExpiryKey)) || pfCartMemory; }
  catch { return pfCartMemory; }
}
function pfWriteExpiry(value) {
  pfCartMemory = value;
  try { if(value)localStorage.setItem(pfCartExpiryKey,JSON.stringify(value));else localStorage.removeItem(pfCartExpiryKey); } catch {}
}
const pfCartURL = path => `${window.Shopify?.routes?.root || '/'}cart${path}.js`;
async function pfGetCart() {
  const response = await fetch(pfCartURL(''), {cache:'no-store'});
  if(!response.ok)throw Error('Kurven kunne ikke hentes');
  return response.json();
}
function pfRenderExpiry() {
  const state = pfReadExpiry();
  const seconds = state ? Math.max(0,Math.ceil((state.deadline-Date.now())/1000)) : 600;
  const text = `${String(Math.floor(seconds/60)).padStart(2,'0')}:${String(seconds%60).padStart(2,'0')}`;
  document.querySelectorAll('[data-cart-expiry]').forEach(el=>{el.textContent=text;el.hidden=!state;});
  document.querySelectorAll('[data-cart-expiry-label]').forEach(el=>el.textContent=state ? (seconds ? 'Din kurv udløber om' : 'Din kurv tømmes…') : 'Din kurv er tom');
}
async function pfExpireCart() {
  if(pfCartClearing || pfCartPending || Date.now()<pfCartRetryAt)return;
  const state=pfReadExpiry();
  if(!state || state.deadline>Date.now())return;
  pfCartClearing=true;
  const clear = async()=>{
    const current=pfReadExpiry();
    if(pfCartPending || !current || current.deadline!==state.deadline || current.deadline>Date.now())return;
    const cart=await pfGetCart();
    if(cart.token!==state.token){pfWriteExpiry(null);pfSyncCart(cart);return;}
    if(!cart.item_count){pfWriteExpiry(null);pfSyncCart(cart);return;}
    if(pfCartPending || pfReadExpiry()?.deadline!==state.deadline)return;
    const deferred=CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({action:'remove',context:'cart',lines:cart.items.map(item=>({merchandiseId:String(item.variant_id),quantity:item.quantity})),promise:deferred.promise}));
    try {
      const response=await fetch(pfCartURL('/clear'),{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sections:'cart-drawer-section'})});
      if(!response.ok)throw Error('Kurven kunne ikke tømmes');
      const empty=await response.json();
      if(pfReadExpiry()?.deadline===state.deadline)pfWriteExpiry(null);
      deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(empty),detail:{items:empty.items,sections:empty.sections,source:'pelsfred-cart-expiry',didError:false}});
      if(location.pathname.replace(/\/$/,'')===`${window.Shopify?.routes?.root || '/'}cart`.replace(/\/$/,''))location.reload();
    }catch(error){deferred.reject(error);throw error;}
  };
  try {
    if(navigator.locks)await navigator.locks.request('pelsfred-cart-expiry',clear);else await clear();
  }catch(error){pfCartRetryAt=Date.now()+5000;console.warn('[cart-expiry]',error);}
  finally{pfCartClearing=false;pfRenderExpiry();}
}
document.addEventListener(StandardEvents.cartLinesUpdate,event=>{
  if(event.action!=='add' || !event.promise)return;
  pfCartPending++;
  event.promise.then(async result=>{
    if(result.detail?.didError)return;
    const cart=await pfGetCart();
    if(cart.item_count)pfWriteExpiry({token:cart.token,deadline:Date.now()+pfCartDuration});
    pfRenderExpiry();
  }).catch(error=>console.warn('[cart-expiry]',error)).finally(()=>pfCartPending--);
});
function pfSyncCart(cart) {
  const deferred=CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({action:'remove',context:'cart',lines:[],promise:deferred.promise}));
  deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{items:cart.items,source:'pelsfred-cart-sync',didError:false}});
}
async function pfInitializeExpiry(sync=false){
  try{
    const cart=await pfGetCart();
    if(pfCartPending)return;
    const state=pfReadExpiry();
    if(!cart.item_count){pfWriteExpiry(null);pfSyncCart(cart);}
    else if(sync)pfSyncCart(cart);
    if(cart.item_count && (!state || state.token!==cart.token))pfWriteExpiry({token:cart.token,deadline:Date.now()+pfCartDuration});
    pfRenderExpiry();await pfExpireCart();
  }catch(error){console.warn('[cart-expiry]',error);}
}
setInterval(()=>{pfRenderExpiry();void pfExpireCart();},1000);
window.addEventListener('storage',event=>{if(event.key===pfCartExpiryKey){pfCartMemory=null;pfRenderExpiry();void pfInitializeExpiry(true);}});
window.addEventListener('pageshow',()=>void pfInitializeExpiry(true));
document.addEventListener('visibilitychange',()=>{if(!document.hidden)void pfInitializeExpiry(true);});
void pfInitializeExpiry();
